import React, {Fragment} from 'react'
import NoSSR from '@components/NoSSR'
import getQueryObject from '@helpers/misc/getQueryObject'
import scrollTo from '@helpers/misc/scrollTo'
import Container from '@packages/justo-parts/lib/components/Container'
import withScroll from '@packages/justo-parts/lib/decorators/withScroll'
import StoreName from '@page-components/Checkout/Checkout/Delivery/StoreName'
import flatten from 'lodash/flatten'
import uniqBy from 'lodash/uniqBy'
import {NextRouter, withRouter} from 'next/router'
import slugify from 'slugify'

import Bar from './Bar'
import BrandBar from './BrandBar'
import Brands from './Brands'
import Categories from './Categories'
import Category from './Category'
import Filters from './Filters'
import getProducts from './getProducts'
import Loading from './Loading'
import NoBrandDivider from './NoBrandDivider'
import SelectProduct from './SelectProduct'

import styles from './styles.module.css'

export interface ProductsContentProps {
  menuId: string
  storeId: string
  categories: any
  preferences: any
  scrollY?: number
  website: any
  hidePrice?: boolean
  layout: string
  productsLayout: string
  nextOpenDate: React.ReactNode
  productCardDesign: string
  router: NextRouter
}

@withScroll
class ProductsContent extends React.Component<ProductsContentProps, any> {
  lastProductId?: string = null
  categoriesRefs = {}
  state: any = {scrollOffset: 0, filter: '', sorting: null, lastScroll: null, searchProducts: []}

  constructor(props: ProductsContentProps) {
    super(props)
    const productId = props.router.query.productId
    if (productId) {
      this.lastProductId = typeof productId === 'string' ? productId : productId[0]
    }
  }

  componentDidMount() {
    const scrollCategorySlug = this.props.router.query.categoria
    const search = this.props.router.query.search

    if (scrollCategorySlug) {
      this.setScrollOnClick(scrollCategorySlug)
    }

    if (search) {
      this.setState({filter: search})
      this.memoizeProducts()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.setScrollToId()
    this.checkActiveCategory()
    this.scrollTo()

    const scrollCategorySlug = this.props.router.query.categoria

    if (scrollCategorySlug && this.state.lastScroll !== scrollCategorySlug) {
      this.setScrollOnClick(scrollCategorySlug)
    }

    const search = this.props.router.query.search

    if (search && search !== prevProps.router.query.search) {
      this.setState({filter: search})
    }

    // If either the query changed or the previous memoization attempt failed, memoize the products
    if (
      search &&
      (search !== prevProps.router.query.search || !this.state.searchProducts?.length)
    ) {
      this.memoizeProducts()
    }
  }

  setScrollOnClick(category) {
    this.setState({
      scrollToSlug: category,
      lastScroll: category
    })
  }

  setScrollToId() {
    const {categories} = this.props
    const {scrollToSlug, scrollTo} = this.state
    if (!categories) return
    if (!scrollToSlug || scrollTo) return

    const clean = text => text.toLowerCase().replace(/ /g, '-')
    const scrollCategory = categories.items.find(
      item => slugify(clean(item.name)) === clean(scrollToSlug)
    )

    if (scrollCategory) {
      this.setState({scrollTo: scrollCategory._id, scrollToSlug: null})
    }
  }

  getVisibleCategories() {
    if (!this.props.categories) return []
    return this.props.categories.items.filter(
      category =>
        category.products.filter(product => {
          if (!product.availabilityAt) return false
          if (product.availabilityAt.notAvailableMessage) return true
          return !!product.availabilityAt.available
        }).length > 0
    )
  }

  getCategoriesBarOffset() {
    if (this.props.layout === 'v1') {
      return 70
    } else {
      return 70 + 50
    }
  }

  scrollTo() {
    if (!this.props.categories) return
    if (!this.state.scrollTo) return

    if (!this.categoriesRefs[this.state.scrollTo]) {
      console.log('scroll to category not found')
      return
    }

    if (!this.categoriesRefs[this.state.scrollTo].offsetTop) {
      console.log('scroll to category not mounted, trying again')
      setTimeout(() => this.scrollTo(), 400)
      return
    }

    const top =
      this.categoriesRefs[this.state.scrollTo].offsetTop -
      this.getCategoriesBarOffset() -
      this.state.scrollOffset

    scrollTo({top})

    this.setState({scrollTo: null})
  }

  checkActiveCategory() {
    const activeCategoryId = this.getActiveCategory()
    if (this.state.activeCategoryId !== activeCategoryId) {
      this.setState({activeCategoryId})
    }
  }

  getActiveCategory() {
    if (!this.props.categories) return
    let activeCategoryId = null
    for (const {_id: categoryId} of this.props.categories.items) {
      const element = this.categoriesRefs[categoryId]
      if (!element) continue
      const position = element.offsetTop
      if (position - this.getCategoriesBarOffset() - 10 < this.props.scrollY) {
        activeCategoryId = categoryId
      }
    }
    return activeCategoryId
  }

  getSelectedProduct() {
    if (!this.props.categories) return null
    const productId = this.lastProductId || this.props.router.query.productId
    if (!productId) return null

    for (const {products} of this.props.categories.items) {
      for (const product of products) {
        if (product._id === productId) {
          return product
        }
      }
    }
  }

  renderCategories() {
    if (!this.props.categories) return <Loading />
    const categories = this.getVisibleCategories()
    let lastBrandId = null
    return categories.map(category => {
      let brand = null
      if (category.brand && category.brand._id !== lastBrandId) {
        brand = category.brand
        lastBrandId = brand._id
      }

      const dividerContent = !category.brand && lastBrandId ? <NoBrandDivider /> : null
      const brandContent = brand ? <BrandBar brand={brand} /> : null

      if (!category.brand && lastBrandId) {
        lastBrandId = null
      }

      return (
        <Fragment key={category._id}>
          {brandContent}
          {dividerContent}
          <div ref={ref => (this.categoriesRefs[category._id] = ref)}>
            <Category
              productsLayout={this.props.productsLayout}
              layout={this.props.layout}
              productCardDesign={this.props.productCardDesign}
              category={category}
              menuId={this.props.menuId}
              hidePrice={this.props.preferences.shouldHidePrice}
              // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
              select={select => this.setState({select, open: true})}
              sorting={this.state.sorting}
              hideNotAvailableProducts={this.props.website.hideNotAvailableProducts}
            />
          </div>
        </Fragment>
      )
    })
  }

  renderSelect() {
    return (
      <NoSSR>
        <SelectProduct
          product={this.getSelectedProduct()}
          storeId={this.props.storeId}
          open={!!(this.props.router.query.productId && this.getSelectedProduct())}
          menuId={this.props.preferences.menuId}
          preferences={this.props.preferences}
          close={() => {
            const query = {...getQueryObject(this.props.router)}
            delete query.productId
            this.props.router.push({pathname: '/pedir', query}, null, {scroll: false})
          }}
        />
      </NoSSR>
    )
  }

  renderBar() {
    const visibleCategories = this.getVisibleCategories()

    if (!visibleCategories.length) return
    return this.props.website && this.props.website.isMultiBrand ? (
      <Brands
        activeCategoryId={this.state.activeCategoryId}
        categories={this.getVisibleCategories()}
        scrollTo={scrollTo => this.setState({scrollTo, scrollOffset: 120})}
        layout={this.props.layout}
        onClick={() => this.setState({filter: null})}
      />
    ) : (
      <Categories
        activeCategoryId={this.state.activeCategoryId}
        categories={this.getVisibleCategories()}
        scrollTo={scrollTo => this.setState({scrollTo})}
        layout={this.props.layout}
        onClick={() => this.setState({filter: null})}
      />
    )
  }

  async memoizeProducts() {
    const categories = this.props?.categories?.items
    if (!categories || categories.length === 0) return

    const Fuse = (await import('fuse.js')).default
    const allProducts = uniqBy(flatten(categories.map(cat => cat.products)), '_id')
    const searchProducts = getProducts(allProducts, this.state.filter, Fuse, this.state.sorting)
    if (searchProducts.length === 0) return

    this.setState({searchProducts})
  }

  renderSearchResults() {
    if (!this.props.categories) return <Loading />

    const category = {
      _id: 'search',
      name: 'Resultados',
      products: this.state.searchProducts
    }
    return (
      <Category
        productsLayout={this.props.productsLayout}
        layout={this.props.layout}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        category={category}
        menuId={this.props.menuId}
        hidePrice={this.props.preferences.shouldHidePrice}
        select={select => this.setState({select, open: true})}
        productCardDesign={this.props.productCardDesign}
        sorting={this.state.sorting}
        hideNotAvailableProducts={this.props.website.hideNotAvailableProducts}
      />
    )
  }

  render() {
    const productId = this.props.router.query.productId
    if (productId) {
      this.lastProductId = typeof productId === 'string' ? productId : productId[0]
    }
    const products = this.state.filter ? this.renderSearchResults() : this.renderCategories()
    return (
      <div className={styles.container + ' ' + styles['container_' + this.props.layout]}>
        <NoSSR>
          <Bar
            layout={this.props.layout}
            filter={this.state.filter}
            setFilter={filter => {
              this.setState({filter})
              if (filter) {
                this.memoizeProducts()
              }
            }}>
            {this.renderBar()}
          </Bar>
        </NoSSR>
        <Container className={styles.containerLabel}>
          <div className={styles.containerIcon}>
            <StoreName preferences={this.props.preferences} website={this.props.website} isOnMenu />
          </div>
          <div className={styles.containerFilter}>
            <NoSSR>
              <Filters
                sorting={this.state.sorting}
                setSorting={sorting => this.setState({sorting})}
              />
            </NoSSR>
          </div>
        </Container>
        {this.props.layout === 'v2' ? (
          <Container>
            {this.props.nextOpenDate}
            <br />
            {products}
          </Container>
        ) : (
          products
        )}
        {this.renderSelect()}
      </div>
    )
  }
}

export default withRouter<ProductsContentProps>(ProductsContent)
